// src/services/assignmentService.ts
import { useAuthenticatedFetch } from './apiService';

export interface AssignmentRequest {
  scenario_id: number;
  emails: string[];
  start_date: string | Date;
  end_date: string | Date;
}

export interface AssignmentResponse {
  created: number;
  skipped?: number;
  updated_docebo_user_id?: number;
  skipped_emails?: string[];
}

export interface Assignment {
  id: number;
  scenario_id: number;
  title: string;
  user_email: string;
  assigned_at?: string;
  start_date?: string;
  end_date: string;
  completed_on: string;
  status?: string;
  feedback_id?: string;
  transcript_id?: number;
  completed_at?: string;
  score?: string;
}

class AssignmentService {
  constructor(
    private readonly authenticatedFetch: (
      url: string,
      options?: RequestInit
    ) => Promise<Response>
  ) {}

  /**
   * Assign a scenario to emails.
   * Ensures start_date and end_date are sent as ISO strings.
   */
  async assignScenario(data: AssignmentRequest): Promise<AssignmentResponse> {
    // Guarantee ISO string format for dates
    const payload = {
      ...data,
      start_date:
        typeof data.start_date === 'string'
          ? data.start_date
          : data.start_date.toISOString(),
      end_date:
        typeof data.end_date === 'string'
          ? data.end_date
          : data.end_date.toISOString(),
    };

    const response = await this.authenticatedFetch('/assignments', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });

    if (!response.ok) {
      throw new Error(`Failed to assign scenario: ${response.statusText}`);
    }

    return response.json();
  }

  async getAssignmentsForScenario(scenarioId: number): Promise<Assignment[]> {
    const response = await this.authenticatedFetch(`/assignments/by-scenario/${scenarioId}`);
    if (!response.ok) {
      throw new Error(`Failed to fetch assignments: ${response.statusText}`);
    }
    return response.json();
  }

  async getAssignments(
    page: number, 
    filters: Record<string,string>,
    sort_by?: string,
    sort_order?: string
  ): Promise<{ assignments: Assignment[]; total: number }> {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== '' && v !== undefined && v !== null)
    );
    if (sort_by) filteredFilters.sort_by = sort_by;
    if (sort_order) filteredFilters.sort_order = sort_order;
    const params = new URLSearchParams(filteredFilters).toString();
    const url = params ? `/assignments/some/${page}?${params}` : `/assignments/some/${page}`;
    const response = await this.authenticatedFetch(url);
    if (!response.ok) {
      throw new Error(`Failed to fetch assignments: ${response.statusText}`);
    }
    const data = await response.json();
    console.log(data)
    // Ensure the response contains both `assignments` and `total`
    return {
      assignments: data.assignments,
      total: data.total
    };
  }

  /**
   * Export assignments with applied filters/sort.
   * @param filters - filter object (e.g. { user_email, status, roleplay_id })
   * @param format - "csv" or "xlsx"
   * @param sort_by - sort field
   * @param sort_order - "asc" or "desc"
   * @returns - The raw fetch Response (for blob download)
   */

  async exportAssignments(
    filters: Record<string, string>,
    format: 'csv' | 'xlsx' = 'csv',
    sort_by?: string,
    sort_order?: string
  ): Promise<Response> {
    const filteredFilters = Object.fromEntries(
      Object.entries(filters).filter(([_, v]) => v !== '' && v !== undefined && v !== null)
    );
    if (sort_by) filteredFilters.sort_by = sort_by;
    if (sort_order) filteredFilters.sort_order = sort_order;
    filteredFilters.format = format;
    const params = new URLSearchParams(filteredFilters).toString();
    const url = `/assignments/export?${params}`;
    return this.authenticatedFetch(url, { method: 'GET' });
  }

  async updateAssignment(assignmentId: number, update: Partial<Assignment>): Promise<Assignment> {
    const response = await this.authenticatedFetch(
      `/assignments/by-scenario/${assignmentId}`,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(update),
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to update assignment: ${response.statusText}`);
    }
    return response.json();
  }

  async deleteAssignment(assignmentId: number): Promise<void> {
    const response = await this.authenticatedFetch(
      `/assignments/by-scenario/${assignmentId}`,
      { method: 'DELETE' }
    );
    if (!response.ok) {
      throw new Error(`Failed to delete assignment: ${response.statusText}`);
    }
    // No response body expected
  }

  /**
   * Download a transcript file by its ID.
   * This returns a Blob you can use to trigger a download in the browser.
   */
  async downloadTranscriptResponse(transcriptId: number): Promise<Response> {
    const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const response = await this.authenticatedFetch(
      `/transcripts/${transcriptId}/download?timezone=${encodeURIComponent(timeZone)}`,
      {
        method: 'GET',
        headers: {
          'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
        }
      }
    );
    if (!response.ok) {
      throw new Error(`Failed to download transcript: ${response.statusText}`);
    }
    return response;
  }
}

/**
* React hook to use AssignmentService
*/
export const useAssignmentService = () => {
  const authenticatedFetch = useAuthenticatedFetch();
  return new AssignmentService(authenticatedFetch);
};

export default useAssignmentService;